import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFolder,
  FaRegFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFileArchive,
} from "react-icons/fa";
import {
  FiChevronDown,
  FiChevronRight,
  FiGrid,
  FiList,
  FiArrowLeft,
  FiHome,
  FiDownload,
} from "react-icons/fi";
import PropTypes from "prop-types";

/**
 * FileDirectory
 * - tree: árbol expandible
 * - icons: explorador tipo Windows (grid) con navegación por carpetas
 * - descarga: botón en carpetas si tienen url
 */
export default function FileDirectory({ data = [], initialView = "tree" }) {
  const [view, setView] = useState(initialView); // "tree" | "icons"
  const [path, setPath] = useState([]); // stack de folderIds (string)

  // Normaliza para vista iconos (no depende de referencias de children)
  const normalized = useMemo(() => normalizeTree(data), [data]);

  const currentIds = useMemo(() => {
    if (view !== "icons") return normalized.rootIds;
    if (path.length === 0) return normalized.rootIds;

    const currentFolderId = path[path.length - 1];
    const folder = normalized.nodes.get(currentFolderId);
    return folder?.childrenIds ?? [];
  }, [view, path, normalized]);

  const items = useMemo(
    () => currentIds.map((id) => normalized.nodes.get(id)).filter(Boolean),
    [currentIds, normalized],
  );

  const canGoBack = path.length > 0;

  const goBack = () => setPath((p) => p.slice(0, -1));
  const goRoot = () => setPath([]);

  const openFolder = (folderId) => {
    const id = String(folderId);
    const node = normalized.nodes.get(id);
    if (!node || node.type !== "folder") return;
    setPath((p) => [...p, id]);
  };

  const breadcrumb = useMemo(() => {
    const crumbs = [{ id: "__root__", name: "Inicio" }];
    for (const id of path) {
      const node = normalized.nodes.get(id);
      if (!node) break;
      crumbs.push({ id, name: node.name });
    }
    return crumbs;
  }, [path, normalized]);

  const jumpToCrumb = (index) => {
    if (index === 0) return goRoot();
    setPath((p) => p.slice(0, index));
  };

  return (
    <div className="w-full rounded-xl border bg-white shadow-sm">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-800">Documentos</div>

          {view === "icons" && (
            <div className="ml-3 flex items-center gap-2">
              <button
                type="button"
                onClick={goBack}
                disabled={!canGoBack}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs
                  ${!canGoBack ? "cursor-not-allowed opacity-40" : "hover:bg-slate-50"}`}
                title="Atrás"
              >
                <FiArrowLeft />
                Atrás
              </button>

              <button
                type="button"
                onClick={goRoot}
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-slate-50"
                title="Inicio"
              >
                <FiHome />
                Inicio
              </button>

              {/* Breadcrumb clickable */}
              <div className="flex max-w-[60vw] items-center gap-1 overflow-hidden text-xs text-slate-600">
                {breadcrumb.map((c, idx) => (
                  <React.Fragment key={c.id}>
                    {idx !== 0 && <span className="text-slate-400">/</span>}
                    <button
                      type="button"
                      onClick={() => jumpToCrumb(idx)}
                      className="truncate hover:underline"
                      title={c.name}
                    >
                      {c.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setView("tree")}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm
              ${view === "tree" ? "border-slate-900 bg-slate-900 text-white" : "hover:bg-slate-50"}`}
            title="Vista de lista"
          >
            <FiList />
            Lista
          </button>

          <button
            type="button"
            onClick={() => setView("icons")}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm
              ${view === "icons" ? "border-slate-900 bg-slate-900 text-white" : "hover:bg-slate-50"}`}
            title="Vista de iconos"
          >
            <FiGrid />
            Iconos
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <AnimatePresence mode="wait" initial={false}>
          {view === "tree" ? (
            <motion.div
              key="tree"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <DirectoryList items={data} level={0} />
            </motion.div>
          ) : (
            <motion.div
              key={`icons:${path.join("/") || "root"}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              <IconGrid items={items} onOpenFolder={(f) => openFolder(f.id)} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** -------------------------------------------------------
 * NORMALIZACIÓN SEGURA
 * ------------------------------------------------------ */
function normalizeTree(data) {
  const nodes = new Map(); // id(string) -> nodeNormalized
  const rootIds = [];

  const visitList = (list, parentId = null) => {
    if (!Array.isArray(list)) return;

    for (const item of list) {
      if (!item || item.id == null) continue;

      const id = String(item.id);
      const type = item.type === "folder" ? "folder" : "file";

      const node = {
        id,
        name: item.name ?? "",
        type,
        fileType: item.fileType,
        url: item.url, // <- importante para descargar carpeta
        parentId,
        childrenIds: [],
      };

      nodes.set(id, node);

      if (!parentId) rootIds.push(id);

      if (type === "folder" && Array.isArray(item.children)) {
        for (const child of item.children) {
          if (child?.id == null) continue;
          node.childrenIds.push(String(child.id));
        }
        visitList(item.children, id);
      }
    }
  };

  visitList(data);

  return { nodes, rootIds };
}

/** =========================
 *  Tree view
 *  ========================= */
function DirectoryList({ items, level }) {
  return (
    <div className="space-y-1">
      {items.map((item) => (
        <DirectoryItem key={item.id} item={item} level={level} />
      ))}
    </div>
  );
}

function DirectoryItem({ item, level }) {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    if (item.type === "folder") setOpen(!open);
  };

  const canDownloadFolder = item.type === "folder" && !!item.url;

  return (
    <div>
      {/* Item Header */}
      <div
        onClick={toggle}
        className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1 hover:bg-gray-100"
        style={{ paddingLeft: `${level * 16}px` }}
      >
        <div className="flex min-w-0 items-center gap-2">
          {item.type === "folder" ? (
            open ? (
              <FiChevronDown size={16} />
            ) : (
              <FiChevronRight size={16} />
            )
          ) : (
            <span className="w-4" />
          )}

          {item.type === "folder" ? (
            <FaFolder size={18} className="text-amber-600" />
          ) : (
            <FileIcon fileType={item.fileType} />
          )}

          {item.type === "file" && item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="truncate text-blue-600 underline hover:text-blue-800"
              title={item.name}
            >
              {item.name}
            </a>
          ) : (
            <span className="truncate text-slate-800" title={item.name}>
              {item.name}
            </span>
          )}
        </div>

        {/* Descarga carpeta (derecha) */}
        {canDownloadFolder && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-white text-slate-700 hover:bg-slate-100"
            title="Descargar carpeta"
          >
            <FiDownload size={14} />
          </a>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && item.children?.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <DirectoryList items={item.children} level={level + 1} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** =========================
 *  Icons view
 *  ========================= */
function IconGrid({ items, onOpenFolder }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {items.map((item) => (
        <IconTile key={item.id} item={item} onOpenFolder={onOpenFolder} />
      ))}
    </div>
  );
}

function IconTile({ item, onOpenFolder }) {
  const isFolder = item.type === "folder";
  const canDownloadFolder = isFolder && !!item.url;

  const handleOpen = () => {
    if (isFolder) onOpenFolder(item);
  };

  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <motion.div
      layout
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="select-none"
    >
      <div
        onClick={handleOpen}
        className={`group relative rounded-xl border bg-white p-3 transition hover:bg-slate-50
          ${isFolder ? "cursor-pointer" : "cursor-default"}
          flex flex-col items-center text-center shadow-[0_1px_0_rgba(0,0,0,0.03)]`}
      >
        {/* Botón descarga carpeta (esquina superior derecha) */}
        {canDownloadFolder && (
          <button
            type="button"
            onClick={handleDownload}
            className="absolute right-2 top-2 inline-flex h-8 w-8
                       items-center justify-center rounded-lg border bg-white/90 text-slate-700
                       opacity-100 shadow-sm transition hover:bg-slate-100 md:opacity-0
                       md:group-hover:opacity-100"
            title="Descargar carpeta"
          >
            <FiDownload size={16} />
          </button>
        )}

        <div className="flex h-14 w-14 items-center justify-center">
          {isFolder ? (
            <FaFolder size={44} className="text-amber-600" />
          ) : (
            <BigFileIcon fileType={item.fileType} />
          )}
        </div>

        {item.type === "file" && item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 line-clamp-2 text-xs text-blue-700 hover:underline"
            title={item.name}
            onClick={(e) => e.stopPropagation()}
          >
            {item.name}
          </a>
        ) : (
          <div
            className="mt-2 line-clamp-2 text-xs text-slate-800"
            title={item.name}
          >
            {item.name}
          </div>
        )}

        {isFolder && (
          <div className="mt-1 text-[11px] text-slate-500 opacity-0 transition group-hover:opacity-100">
            Abrir carpeta
          </div>
        )}
      </div>
    </motion.div>
  );
}

/** =========================
 *  File icons
 *  ========================= */
function FileIcon({ fileType }) {
  const colorMap = {
    pdf: "text-red-500",
    dwg: "text-yellow-500",
    img: "text-blue-500",
    zip: "text-purple-500",
  };

  const iconMap = {
    pdf: FaFilePdf,
    img: FaFileImage,
    dwg: FaFileArchive,
    zip: FaFileArchive,
  };

  const Icon = iconMap[fileType] ?? FaRegFileAlt;
  return <Icon size={18} className={colorMap[fileType] ?? "text-gray-600"} />;
}

function BigFileIcon({ fileType }) {
  const colorMap = {
    pdf: "text-red-500",
    dwg: "text-yellow-500",
    img: "text-blue-500",
    zip: "text-purple-500",
  };

  const iconMap = {
    pdf: FaFilePdf,
    img: FaFileImage,
    dwg: FaFileArchive,
    zip: FaFileArchive,
  };

  const Icon = iconMap[fileType] ?? FaRegFileAlt;
  return <Icon size={44} className={colorMap[fileType] ?? "text-slate-500"} />;
}

/** =========================
 *  PropTypes
 *  ========================= */
FileDirectory.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["folder", "file"]).isRequired,
      fileType: PropTypes.string,
      url: PropTypes.string, // <- carpeta o archivo
      children: PropTypes.array,
    }),
  ),
  initialView: PropTypes.oneOf(["tree", "icons"]),
};

DirectoryList.propTypes = {
  items: PropTypes.array.isRequired,
  level: PropTypes.number.isRequired,
};

DirectoryItem.propTypes = {
  item: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
};

IconGrid.propTypes = {
  items: PropTypes.array.isRequired,
  onOpenFolder: PropTypes.func.isRequired,
};

IconTile.propTypes = {
  item: PropTypes.object.isRequired,
  onOpenFolder: PropTypes.func.isRequired,
};

FileIcon.propTypes = { fileType: PropTypes.string };
BigFileIcon.propTypes = { fileType: PropTypes.string };
